export interface RunScorePayload {
  stage: string;
  kills: number;
  time: number;
  victory: boolean;
  level: number;
}

export interface RunProof {
  v: 1;
  tainted: boolean;
  eventCount: number;
  eventHash: string;
  sig: string;
  token?: string;
}

const RUN_SALT_A = 0xa5a55a5a;
const RUN_SALT_B = 0x5851f42d;

function fnvPair(input: string, seedA = RUN_SALT_A, seedB = RUN_SALT_B): string {
  let h1 = seedA >>> 0;
  let h2 = seedB >>> 0;
  for (let i = 0; i < input.length; i += 1) {
    const c = input.charCodeAt(i);
    h1 ^= c;
    h1 = Math.imul(h1, 0x01000193) >>> 0;
    h2 ^= c ^ ((i & 0xff) << 3);
    h2 = Math.imul(h2, 0x85ebca6b) >>> 0;
  }
  return `${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

export function deepFreeze<T>(obj: T): T {
  if (!obj || typeof obj !== 'object' || Object.isFrozen(obj)) return obj;
  Object.freeze(obj);
  for (const key of Object.keys(obj as Record<string, unknown>)) {
    const val = (obj as Record<string, unknown>)[key];
    if (val && typeof val === 'object') deepFreeze(val);
  }
  return obj;
}

export function maxPlausibleLevel(kills: number, time: number): number {
  const k = Math.max(0, Number.isFinite(kills) ? kills : 0);
  const t = Math.max(0, Number.isFinite(time) ? time : 0);
  return Math.min(200, 1 + Math.floor(k * 2.5 + t * 0.15) + 5);
}

// Copy of max(KILL_RATE) in server/limits.mjs: the browser can only warn, the server enforces the limit per realm.
const MAX_KILL_RATE = 12;

export function isPlausibleRun(score: RunScorePayload, stageDuration = 480): boolean {
  if (!score || typeof score !== 'object') return false;
  const { kills, time, victory, level } = score;
  if (!Number.isFinite(kills) || kills < 0 || kills > 30_000) return false;
  if (!Number.isFinite(time) || time < 0 || time > 3600) return false;
  if (!Number.isFinite(level) || level < 1 || level > maxPlausibleLevel(kills, time)) return false;
  if (kills > Math.floor(MAX_KILL_RATE * time + 150)) return false;
  if (victory && (time < stageDuration || kills < 1)) return false;
  return true;
}

export function computeRunSignature(score: RunScorePayload, eventCount: number, eventHash: string): string {
  const timeTenths = Math.round(score.time * 10);
  const payload = `${score.stage}|${Math.floor(score.kills)}|${timeTenths}|${score.victory ? 1 : 0}|${Math.floor(score.level)}|${Math.floor(eventCount)}|${eventHash}`;
  return `rp1:${fnvPair(payload)}`;
}

export function verifyRunProof(score: RunScorePayload, proof: unknown): boolean {
  if (!proof || typeof proof !== 'object') return false;
  const p = proof as Partial<RunProof>;
  if (p.v !== 1 || p.tainted === true) return false;
  if (typeof p.eventCount !== 'number' || !Number.isFinite(p.eventCount) || p.eventCount < 1 || p.eventCount > 1_000_000) return false;
  if (typeof p.eventHash !== 'string' || !/^[0-9a-f]{16}$/.test(p.eventHash)) return false;
  if (typeof p.sig !== 'string') return false;
  if (!isPlausibleRun(score)) return false;
  return p.sig === computeRunSignature(score, p.eventCount, p.eventHash);
}

export type RunEventKind = 'start' | 'kill' | 'level' | 'chest' | 'shrine' | 'boss' | 'end';

export class RunIntegrityGuard {
  tainted = false;
  readonly reasons = new Set<string>();
  eventCount = 0;
  eventHash = fnvPair('init');
  private wallStartMs = 0;
  private liveSimSeconds = 0;

  reset(stageId: string): void {
    this.tainted = false;
    this.reasons.clear();
    this.eventCount = 1;
    this.eventHash = fnvPair(`start:${stageId}`);
    this.wallStartMs = Date.now();
    this.liveSimSeconds = 0;
  }

  markTainted(reason: string): void {
    this.tainted = true;
    this.reasons.add(reason);
  }

  recordEvent(kind: RunEventKind, value = 0): void {
    this.eventCount += 1;
    this.eventHash = fnvPair(`${this.eventHash}:${kind}:${Math.floor(value)}`);
  }

  recordLiveStep(dt: number, timeScale: number, godMode: boolean): void {
    if (godMode) this.markTainted('god_mode');
    if (Math.abs(timeScale - 1) > 0.001) this.markTainted('time_scale');
    if (dt > 0 && Number.isFinite(dt)) {
      this.liveSimSeconds += dt;
      const elapsedWallMs = Math.max(1, Date.now() - this.wallStartMs);
      this.checkWallClock(this.liveSimSeconds, elapsedWallMs, timeScale);
    }
  }

  checkWallClock(accumulatedGameSeconds: number, elapsedWallMs: number, timeScale: number): void {
    if (Math.abs(timeScale - 1) > 0.001) {
      this.markTainted('time_scale');
      return;
    }
    if (accumulatedGameSeconds < 10) return;
    const wallSeconds = Math.max(0.001, elapsedWallMs / 1000);
    if (accumulatedGameSeconds > wallSeconds * 1.45 + 3) {
      this.markTainted('speedhack');
    }
  }

  buildProof(score: RunScorePayload, token?: string | null): RunProof {
    if (this.eventCount < 1) this.recordEvent('end', score.kills);
    const sig = computeRunSignature(score, this.eventCount, this.eventHash);
    const proof: RunProof = {
      v: 1,
      tainted: this.tainted,
      eventCount: this.eventCount,
      eventHash: this.eventHash,
      sig,
    };
    if (token) proof.token = token;
    return proof;
  }
}
